// ─── UTILITY FUNCTIONS ────────────────────────────────────────────────────────
/**
 * Convert a title to a URL-friendly slug
 * @param {string} text - The text to convert
 * @returns {string} - The slug
 */
export const normalizeTripImage = (img) => ({
  ...img,
  fitMode: img?.fitMode === 'contain' ? 'contain' : 'cover',
  focalY: typeof img?.focalY === 'number' ? img.focalY : 50,
});

export const normalizeTripImages = (images) =>
  (images || []).map(normalizeTripImage);

export const generateSlug = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .trim()
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-'); // Replace multiple hyphens with single hyphen
};

/**
 * Ensure all data items have slugs - generates them if missing
 * This is used for data loaded from Firebase that might not have slugs yet
 */
export const ensureSlugs = (data) => {
  if (!data) return data;
  
  return {
    ...data,
    trips: (data.trips || []).map(trip => ({
      ...trip,
      slug: trip.slug || generateSlug(trip.title),
    })),
    events: (data.events || []).map(event => ({
      ...event,
      slug: event.slug || generateSlug(event.title),
    })),
    posts: (data.posts || []).map(post => ({
      ...post,
      slug: post.slug || generateSlug(post.title),
    })),
    corporate: data.corporate ? {
      ...data.corporate,
      packages: (data.corporate.packages || []).map(pkg => ({
        ...pkg,
        slug: pkg.slug || generateSlug(pkg.title),
      })),
    } : data.corporate,
  };
};

// ─── DEFAULT DATA ─────────────────────────────────────────────────────────────
export const defaultData = {
  categories: [
    { id: 'hiking',   label: 'Hiking',   color: '#2d7a3e', bg: '#e6f4ea' },
    { id: 'walking',  label: 'Walking',  color: '#1a56c4', bg: '#e8f0fe' },
    { id: 'activity', label: 'Activity', color: '#e65100', bg: '#fff3e0' },
  ],
  trips: [
    {
      id: 1,
      title: "Ramadan Hike – The Silent Island",
      slug: generateSlug("Ramadan Hike – The Silent Island"),
      titleAr: "رحلة رمضان – سد الملك طلال",
      location: "Jerash",
      distance: "6 KM",
      time: "2:00 PM",
      level: "Moderate",
      fees: "25 JDs",
      date: "Saturday 14/03",
      category: "hiking",
      thisWeek: true,
      softDeleted: false,
      images: [
        { url: "https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&q=80", isMain: true, fitMode: 'cover', focalY: 50 },
        { url: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80", isMain: false, fitMode: 'cover', focalY: 50 },
        { url: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&q=80", isMain: false, fitMode: 'cover', focalY: 50 },
      ],
      description: "Explore the breathtaking landscapes around the Silent Island dam in Jerash. A moderate trail with stunning panoramic views over the valley.",
      highlights: [
        "Wander through thick forests filled with pine and oak trees",
        "Experience peaceful nature trails away from the city hustle",
        "Breathe fresh mountain air and enjoy scenic views",
        "Ideal for hikers looking for a gentle yet engaging outdoor adventure",
      ],
      included: "Transportation, breakfast, snacks, lunch, water, and guide.",
    },
    {
      id: 2,
      title: "Ramadan Hiking – Zubia Forest",
      slug: generateSlug("Ramadan Hiking – Zubia Forest"),
      titleAr: "رحلة رمضان – غابات زوبيا",
      location: "Ajloun",
      distance: "5 KM",
      time: "1:00 AM",
      level: "Moderate",
      fees: "25 JDs",
      date: "Friday 13/3",
      category: "hiking",
      thisWeek: true,
      softDeleted: false,
      images: [
        { url: "https://images.unsplash.com/photo-1448375240586-882707db888b?w=800&q=80", isMain: true, fitMode: 'cover', focalY: 50 },
        { url: "https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&q=80", isMain: false, fitMode: 'cover', focalY: 50 },
      ],
      description: "Walk through the dense pine forests of Zubia in Ajloun, one of Jordan's most beautiful natural reserves.",
    },
    {
      id: 3,
      title: "Ramadan Hike – Zeglab Dam",
      slug: generateSlug("Ramadan Hike – Zeglab Dam"),
      titleAr: "رحلة رمضان – سد زقلاب",
      location: "Irbid",
      distance: "5 KM",
      time: "12:00 PM",
      level: "Moderate",
      fees: "25 JDs",
      date: "Friday 13/03",
      category: "hiking",
      thisWeek: false,
      softDeleted: false,
      images: [
        { url: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80", isMain: true, fitMode: 'cover', focalY: 50 },
      ],
      description: "A scenic hike around the beautiful Zeglab Dam reservoir surrounded by rolling green hills and wildflowers.",
    },
    {
      id: 4,
      title: "Walk & Sahur – Amman City",
      slug: generateSlug("Walk & Sahur – Amman City"),
      titleAr: "مشوار وسحور – عمّان",
      location: "Amman",
      distance: "2 KM",
      time: "9:00 PM",
      level: "Easy",
      fees: "10 JDs",
      date: "Thursday 12/3",
      category: "walking",
      thisWeek: true,
      softDeleted: false,
      images: [
        { url: "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=800&q=80", isMain: true, fitMode: 'cover', focalY: 50 },
        { url: "https://images.unsplash.com/photo-1504893524553-b855bce32c67?w=800&q=80", isMain: false, fitMode: 'cover', focalY: 50 },
      ],
      description: "Evening walk through the illuminated streets of Amman followed by a traditional Sahur meal together.",
    },
    {
      id: 5,
      title: "Hunter's Night – Archery",
      slug: generateSlug("Hunter's Night – Archery"),
      titleAr: "ليالي الصياد – رماية",
      location: "Amman",
      distance: "-",
      time: "9:00 PM",
      level: "All Levels",
      fees: "10 JDs",
      date: "Monday 09/3",
      category: "activity",
      thisWeek: false,
      softDeleted: false,
      images: [
        { url: "https://images.unsplash.com/photo-1504893524553-b855bce32c67?w=800&q=80", isMain: true, fitMode: 'cover', focalY: 50 },
      ],
      description: "Try your hand at archery in this special night gathering event. Great for beginners and experienced archers alike.",
    },
    {
      id: 6,
      title: "Badminton Night – Khalda",
      slug: generateSlug("Badminton Night – Khalda"),
      titleAr: "بادمنتون – خلدا",
      location: "Khalda, Amman",
      distance: "-",
      time: "10:00 PM",
      level: "All Levels",
      fees: "8 JDs",
      date: "Wednesday 12/3",
      category: "activity",
      thisWeek: false,
      softDeleted: false,
      images: [
        { url: "https://images.unsplash.com/photo-1626224583764-f87db24ac4ea?w=800&q=80", isMain: true, fitMode: 'cover', focalY: 50 },
      ],
      description: "Fun competitive badminton evening at AlAsriyya Schools in Khalda. Join us for a great workout and social time.",
    },
    {
      id: 7,
      title: "Ramadan Hike – Alsafsaf Forest",
      slug: generateSlug("Ramadan Hike – Alsafsaf Forest"),
      titleAr: "رحلة رمضان – غابات الصفصاف",
      location: "Ajloun",
      distance: "4 KM",
      time: "2:00 PM",
      level: "Easy to Moderate",
      fees: "25 JDs",
      date: "Friday 06/3",
      category: "hiking",
      thisWeek: false,
      softDeleted: false,
      images: [
        { url: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&q=80", isMain: true, fitMode: 'cover', focalY: 50 },
      ],
      description: "Discover the Alsafsaf willow forest, a hidden gem nestled in the rolling hills of Ajloun.",
    },
  ],
  about: {
    title: "Your Experience Experts in Jordan",
    titleAr: "خبراء التجارب في الأردن",
    body: "Keepers Experience (الحراس) is Jordan's premier outdoor adventure company. We organize weekly hikes, private trips, and corporate team-building experiences with a deep focus on nature, community, and authentic Jordanian landscapes. With over 33,000 followers on Instagram and 5,000+ happy hikers, we are the go-to adventure community in Jordan.",
    bodyAr: "كيبرز إكسبيرينس (الحراس) هي الشركة الرائدة في مجال المغامرات الخارجية في الأردن. ننظم رحلات أسبوعية وخاصة وفعاليات بناء فريق للشركات مع التركيز على الطبيعة والمجتمع والمناظر الأردنية الأصيلة.",
    stats: [
      { label: "Happy Hikers", value: "5000+" },
      { label: "Trails Explored", value: "120+" },
      { label: "Years Active", value: "6+" },
      { label: "Instagram Followers", value: "33K" },
    ],
  },
  hero: {
    headline: "Explore Jordan's Wild Beauty",
    subheadline: "Weekly hikes · Private trips · Corporate adventures",
    cta: "View Upcoming Trips",
    badge: "🌿 Jordan's #1 Hiking Community",
  },
  contact: {
    phone: "+962 77 611 3530",
    email: "experience@keepersoftheforest.com",
    location: "Amman, Jordan",
    instagram: "@keepers.experience",
    instagramUrl: "https://www.instagram.com/keepers.experience/",
    facebook: "Keepers Experience Jordan",
    facebookUrl: "https://www.facebook.com/keepers.experience/",
    whatsapp: "+962 77 611 3530",
  },
  corporate: {
    heroImage: "https://images.unsplash.com/photo-1522770179533-24471fcdba45?w=1920&q=80",
    headline: "Team Building Experiences",
    tagline: "Stronger teams. Smarter strategies. Memorable moments.",
    description: "At Keepers Experience, we design corporate trips and team-building programs that go beyond the ordinary. Whether it's a day in the forest, a desert retreat, or a creative experience by the sea, our activities are tailored to build trust, spark collaboration, and strengthen company culture.",
    services: [
      {
        id: 1,
        icon: "🎯",
        title: "Team-Building Games & Activities",
        body: "From problem-solving challenges to creative communication tasks, designed to bring teams closer.",
      },
      {
        id: 2,
        icon: "🗺️",
        title: "Scavenger & Treasure Hunts",
        body: "Fun, interactive adventures that inspire strategic thinking and teamwork in real time.",
      },
      {
        id: 3,
        icon: "🧭",
        title: "Strategy & Direction Workshops",
        body: "Guided sessions to help teams align, innovate, and move forward with clarity.",
      },
      {
        id: 4,
        icon: "✨",
        title: "Tailored Environments",
        body: "Whether it's high-energy fun, peaceful wellness-focused retreats, or goal-oriented strategic days, we craft experiences that reflect your company's values and objectives.",
      },
    ],
    packages: [
      {
        id: 1,
        title: "Forest Team Day",
        slug: generateSlug("Forest Team Day"),
        destination: "Ajloun Forest",
        employees: "20 – 50",
        price: "45 JD / person",
        details: "A full day in Ajloun exploring nature trails, team challenges, and group games. Includes BBQ lunch. Transportation not included.",
        image: "https://images.unsplash.com/photo-1448375240586-882707db888b?w=800&auto=format&fit=crop",
      },
      {
        id: 2,
        title: "Desert Strategy Retreat",
        slug: generateSlug("Desert Strategy Retreat"),
        destination: "Wadi Rum",
        employees: "10 – 30",
        price: "120 JD / person",
        details: "2-day retreat in Wadi Rum. Includes overnight stay in a luxury camp, strategy workshops, and a guided desert hike. Transportation from Amman included.",
        image: "https://images.unsplash.com/photo-1509316785289-025f5b846b35?w=800&auto=format&fit=crop",
      },
      {
        id: 3,
        title: "City Walk & Workshop",
        slug: generateSlug("City Walk & Workshop"),
        destination: "Amman Downtown",
        employees: "Up to 100",
        price: "25 JD / person",
        details: "Half-day urban exploration with collaborative challenges and a creative workshop. Perfect for large teams with any fitness level.",
        image: "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=800&auto=format&fit=crop",
      },
    ],
    clients: [
      { id: 1, name: "Microsoft", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/96/Microsoft_logo_%282012%29.svg/3840px-Microsoft_logo_%282012%29.svg.png" },
      { id: 2, name: "Arab Bank", logo: "https://logo.clearbit.com/arabbank.com" },
      { id: 3, name: "Google",    logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/2f/Google_2015_logo.svg/320px-Google_2015_logo.svg.png" },
      { id: 4, name: "Samsung",   logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/24/Samsung_Logo.svg/320px-Samsung_Logo.svg.png" },
    ],
  },
  privacyPolicy: {
    lastUpdated: "April 2026",
    sections: [
      {
        id: 1,
        title: "Introduction",
        body: "Welcome to Keepers Experience (الحراس). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, and safeguard your data when you interact with our website, book trips, or contact us through any channel.",
      },
      {
        id: 2,
        title: "Information We Collect",
        body: "We collect information you voluntarily provide when registering for trips or contacting us, including your name, phone number, email address, and any special requirements. We may also collect basic technical data such as your browser type and IP address through standard web analytics.",
      },
      {
        id: 3,
        title: "How We Use Your Information",
        body: "Your information is used exclusively to confirm bookings, send trip updates and reminders, respond to your inquiries, and improve our services. We do not use your data for unsolicited marketing without your consent.",
      },
      {
        id: 4,
        title: "WhatsApp & Communication",
        body: "When you contact us via WhatsApp or submit a booking inquiry, our team may follow up by phone, WhatsApp, or email to confirm your reservation. These communications are strictly related to your trip and will not be shared with third parties.",
      },
      {
        id: 5,
        title: "Data Sharing",
        body: "We do not sell, trade, or rent your personal information to third parties. We may share limited data with trusted partners (such as transportation providers) only as necessary to fulfill your booking, and solely for that purpose.",
      },
      {
        id: 6,
        title: "Data Security",
        body: "We take reasonable technical and organizational measures to protect your personal data from unauthorized access, loss, or misuse. However, no method of transmission over the internet is completely secure, and we cannot guarantee absolute security.",
      },
      {
        id: 7,
        title: "Your Rights",
        body: "You have the right to request access to, correction of, or deletion of your personal data at any time. To exercise these rights, please contact us directly via email or WhatsApp. We will respond to your request within a reasonable timeframe.",
      },
      {
        id: 8,
        title: "Contact Us",
        body: "If you have any questions or concerns about this Privacy Policy, please reach out to us at info@keepers.jo or through our WhatsApp number. We are happy to address any privacy-related inquiries.",
      },
    ],
  },
  posts: [
    {
      id: 1,
      title: "Trail Review: Zubia Forest in Spring",
      slug: "trail-review-zubia-forest",
      category: "trip-report",
      coverImage: "https://images.unsplash.com/photo-1448375240586-882707db888b?w=900&q=80",
      date: "March 2026",
      author: "Keepers Team",
      excerpt: "A peaceful walk through one of Jordan's most beautiful pine forests — here's everything you need to know before visiting Zubia in spring.",
      published: true,
      sections: [
        { id: 1, heading: "Getting There", body: "Zubia Forest is located in Ajloun Governorate, about 75 km from Amman. The drive takes around 1.5 hours and the road is well-paved. Parking is available near the forest entrance." },
        { id: 2, heading: "The Trail", body: "The main loop is approximately 5 km and takes 2–3 hours at a comfortable pace. The path winds through dense pine trees with occasional views of the surrounding valleys. The terrain is mostly flat with a few gentle slopes — suitable for all fitness levels." },
        { id: 3, heading: "What to Bring", body: "Comfortable walking shoes are a must. Bring at least 1.5L of water per person, a light snack, and sunscreen for open sections. In spring the weather can change quickly so a light jacket is recommended." },
        { id: 4, heading: "Our Experience", body: "We ran this trail as part of our Ramadan series and it was truly magical. The scent of pine in the cool morning air, birds singing, and the sound of wind through the trees made for an unforgettable experience. Highly recommended for families and beginners." },
      ],
    },
    {
      id: 2,
      title: "5 Tips for Your First Hiking Trip in Jordan",
      slug: "5-tips-first-hike-jordan",
      category: "tips",
      coverImage: "https://images.unsplash.com/photo-1551632811-561732d1e306?w=900&q=80",
      date: "February 2026",
      author: "Keepers Team",
      excerpt: "Thinking about joining your first hiking trip? Here are five practical tips that will make your experience safer, more comfortable and a lot more fun.",
      published: true,
      sections: [
        { id: 1, heading: "1. Start with an Easy Trail", body: "Don't jump straight into a difficult mountain hike. Start with a 3–5 km trail rated Easy or Easy to Moderate. Build your stamina over a few trips before tackling harder routes." },
        { id: 2, heading: "2. Wear the Right Shoes", body: "Regular sneakers often work fine for easy trails, but if you're hiking on rocky or uneven terrain, proper trail shoes make a huge difference in comfort and stability." },
        { id: 3, heading: "3. Hydration is Everything", body: "Jordan's climate can be warm even in winter on sunny days. Drink water regularly throughout the hike — don't wait until you're thirsty. Aim for at least 500ml per hour on active hikes." },
        { id: 4, heading: "4. Tell Someone Where You're Going", body: "Always let a friend or family member know your trail, estimated return time, and who you're going with. Better yet, join an organized group like ours for added safety." },
        { id: 5, heading: "5. Come Earlier Than You Think", body: "Being on time for the group meeting point is crucial. Arriving 10 minutes early lets you settle in, meet other hikers, and start relaxed rather than rushed." },
      ],
    },
    {
      id: 3,
      title: "Spring Season 2026 — What's Coming",
      slug: "spring-2026-announcement",
      category: "news",
      coverImage: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=900&q=80",
      date: "April 2026",
      author: "Keepers Team",
      excerpt: "We've got an exciting lineup of trails and experiences planned for spring 2026. From forest hikes to overnight desert retreats — here's what's coming.",
      published: true,
      sections: [
        { id: 1, heading: "New Trails This Season", body: "We're adding three new trails to our regular schedule this spring: a scenic loop around Dibeen Forest, a night hike in Dana Nature Reserve, and a dawn hike up to Ajloun Castle. Details and dates will be announced on our Instagram soon." },
        { id: 2, heading: "Corporate Season Kicks Off", body: "April marks the start of our most active corporate season. We have several company team-building programs already booked across Wadi Rum and Ajloun. If you're interested in booking a corporate experience, reach out now before spots fill up." },
        { id: 3, heading: "Join the Community", body: "We're now at over 33,000 followers on Instagram and our WhatsApp community has grown to hundreds of active members who share trail updates, tips, and photos. If you're not in the group yet, message us to join." },
      ],
    },
  ],
  events: [
    {
      id: 1,
      title: "Desert Game Night – Wadi Rum",
      slug: generateSlug("Desert Game Night – Wadi Rum"),
      location: "Wadi Rum",
      date: "Friday 10/04",
      time: "8:00 PM",
      fees: "35 JDs",
      groupSize: "10–40 people",
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80",
      description: "An unforgettable evening of team games under the Wadi Rum stars. Includes a desert BBQ and a guided star-gazing session after the games.",
      tags: ["Desert", "Stargazing", "Team Games"],
      upcoming: true,
      published: true,
    },
    {
      id: 2,
      title: "Seaside Game Night – Aqaba",
      slug: generateSlug("Seaside Game Night – Aqaba"),
      location: "Aqaba",
      date: "Saturday 18/04",
      time: "7:00 PM",
      fees: "30 JDs",
      groupSize: "15–60 people",
      image: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=80",
      description: "Laughter-filled team challenges on the shores of the Red Sea. Games, music, and fresh sea air — the perfect social evening for friends or colleagues.",
      tags: ["Sea", "Social", "Night Out"],
      upcoming: true,
      published: true,
    },
    {
      id: 3,
      title: "Forest Game Night – Ajloun",
      slug: generateSlug("Forest Game Night – Ajloun"),
      location: "Ajloun Forest",
      date: "Friday 24/04",
      time: "6:00 PM",
      fees: "28 JDs",
      groupSize: "10–35 people",
      image: "https://images.unsplash.com/photo-1448375240586-882707db888b?w=800&q=80",
      description: "Creative team games in one of Jordan's most beautiful pine forests. Includes a sunset hike before the games and a bonfire to wrap up the evening.",
      tags: ["Forest", "Bonfire", "Creative Games"],
      upcoming: false,
      published: true,
    },
  ],
};
